import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Bar } from 'react-chartjs-2';
import { DataContext } from '../contexts/DataContext';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

const ShipmentModeBarChart = () => {
  const { shipments, isLoading } = useContext(DataContext);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" />
        <span className="ml-2 text-lg">Loading...</span>
      </div>
    );
  }

  if (!shipments) {
    return <div>No data available</div>;
  }

  // Counting shipments by 'mode' value
  const modeCounts = shipments.reduce((acc, shipment) => {
    acc[shipment.mode] = (acc[shipment.mode] || 0) + 1;
    return acc;
  }, {});

  const modeLabels = Object.keys(modeCounts);
  const modeData = Object.values(modeCounts);

  const backgroundColors = [
    'rgba(25, 99, 201, 0.8)',  // Darker shade of blue
    'rgba(21, 86, 175, 0.8)',  // Even darker
    'rgba(17, 73, 149, 0.8)',  // More towards navy blue
    'rgba(13, 60, 123, 0.8)',  // Deeper navy blue
    'rgba(10, 50, 100, 0.8)'   // Near to dark navy
  ];
  
  const borderColors = [
    'rgba(25, 99, 201, 1)',
    'rgba(21, 86, 175, 1)',
    'rgba(17, 73, 149, 1)',
    'rgba(13, 60, 123, 1)',
    'rgba(10, 50, 100, 1)'
  ];

  const chartData = {
    labels: modeLabels,
    datasets: [
      {
        label: 'Number of Shipments by Mode',
        data: modeData,
        backgroundColor: backgroundColors.slice(0, modeData.length), // Use only as many colors as needed
        borderColor: borderColors.slice(0, modeData.length),
        borderWidth: 1,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
      },
    },
    plugins: {
      datalabels: {
        color: '#f8f8f8', // Near white color for labels inside the bars
        font: {
          size: 14,
          family: 'Inter, sans-serif'
        },
        anchor: 'center', // Ensures labels are centered in the bar
        align: 'center', // Ensures labels are aligned in the middle vertically
        formatter: (value, ctx) => value, // Displays the value as the label
      },
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 12,
            family: 'Inter, sans-serif',
            weight: '400',
            color: '#333'
          },
          boxWidth: 20,
          padding: 20,
        },
      },
      tooltip: {
        bodyFont: {
          size: 14,
          family: 'Inter, sans-serif',
          color: '#333'
        },
        titleFont: {
          size: 16,
          family: 'Inter, sans-serif',
          color: '#333'
        },
        backgroundColor: '#ffffff',
        borderColor: '#cccccc',
        borderWidth: 1,
        titleColor: '#333',
        bodyColor: '#333'
      }
    },
  };
  

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg h-custom-card-big flex flex-col justify-between">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartBar} className="text-blue-600 mr-2" />
          Shipment Mode Overview
        </h2>
      </div>
      <div className="flex-grow relative">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
  
};

export default ShipmentModeBarChart;
